import { IExcelReportData, ITableData } from 'types/excelExport';
import { IProFormaGeneralInfo, IOperatingAndPermanentDebtInfo } from 'views/ProFormaTable/types';
import { ProFormaModelIds } from 'constants/proForma';
import assertNever from 'helpers/assertNever';
import {
    createHeaderRow,
    createDataRow,
    createBlankRow,
    createLabelValueRow,
    formatDate,
    formatMonths,
    formatNumber,
    parseNumber,
} from './helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';
import { getModelExportConfig } from './modelConfig';

const getParkingRatioDenominator = (
    modelId: ProFormaModelIds,
    grossSF: number,
    totalRentableSf: number | undefined,
    estimatedUnits: number | null | undefined,
): number | null => {
    switch (modelId) {
        case ProFormaModelIds.INDUSTRIAL:
            return grossSF ? grossSF / 1000 : null;
        case ProFormaModelIds.OFFICE:
        case ProFormaModelIds.RETAIL:
            return totalRentableSf ? totalRentableSf / 1000 : null;
        case ProFormaModelIds.DATA_CENTER:
        case ProFormaModelIds.APARTMENT:
        case ProFormaModelIds.APARTMENT_WITH_RETAIL:        
        case ProFormaModelIds.STUDENT_HOUSING:
        case ProFormaModelIds.LAND_DEVELOPMENT:
        case ProFormaModelIds.RESIDENTIAL_CONDO:
        case ProFormaModelIds.HOTEL_HOSPITALITY:
        case ProFormaModelIds.SELF_STORAGE:
            return estimatedUnits ?? null;
        default:
            return assertNever(modelId);
    }
};

interface IBuildingInfoSheetParams {
    proForma: IProFormaGeneralInfo;
    operatingInfo: IOperatingAndPermanentDebtInfo;
    totalRentableSf?: number;
    retailRentableSf?: number;
    retailUnitCount?: number;
    totalBeds?: number;
}

export const formatBuildingInfoSheet = ({
    proForma,
    operatingInfo,
    totalRentableSf,
    retailRentableSf,
    retailUnitCount,
    totalBeds,
}: IBuildingInfoSheetParams): IExcelReportData => {
    const config = getModelExportConfig(proForma.proFormaModelId);
    const { buildingInfoTab } = config;

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const baseGrossSF = parseNumber(proForma.builtGrossSquareFootage) || 0;
    const retailGrossSF = parseNumber(proForma.retailBuiltGrossSquareFootage) || 0;
    const grossSF = isApartmentWithRetail ? baseGrossSF + retailGrossSF : baseGrossSF;
    // For apartment-with-retail the backend's `total_rentable_sf` already
    // equals `apartment_rentable_sf + retail_total_square_footage` (see
    // backend/utils/pro_forma/apartment/operating_period/income_summary.py),
    // so use it as-is — don't add `retailRentableSf` again.
    const combinedRentableSF = totalRentableSf;
    // For apartment-with-retail keep the displayed efficiency ratio internally
    // consistent with the displayed combined Gross SF / Rentable SF rows by
    // deriving it from those two numbers; fall back to `proForma.efficiencyRatio`
    // (apartment-only) when either input is missing. Other models continue to
    // use `proForma.efficiencyRatio` directly.
    const derivedCombinedEfficiencyRatio = combinedRentableSF && grossSF
        ? combinedRentableSF / grossSF
        : undefined;
    const efficiencyRatio = isApartmentWithRetail && derivedCombinedEfficiencyRatio !== undefined
        ? derivedCombinedEfficiencyRatio
        : parseNumber(proForma.efficiencyRatio);

    const topHeaderRows: ITableData[] = [
        createDataRow([{
            value: proForma.dealName || proForma.name,
            cellFont: { bold: true, size: 14 },
            width: 30,
        }]),
        createDataRow([{ value: 'Building Information', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(2),
    ];

    // Property Information
    const propertyInfoRows: ITableData[] = [
        createHeaderRow(['PROPERTY INFORMATION', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.propertyInfo.dealName, proForma.dealName || proForma.name, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.address, proForma.address, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.city, proForma.city, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.state, proForma.state, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.zip, proForma.zipCode, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.county, proForma.county, true),
    ];

    if (buildingInfoTab.propertyInfo.associatedUniversity) {
        propertyInfoRows.push(createLabelValueRow(
            buildingInfoTab.propertyInfo.associatedUniversity,
            proForma.associatedUniversity,
            true,
        ));
    }

    propertyInfoRows.push(createBlankRow(2));

    // Building Specifications
    const buildingSpecRows: ITableData[] = [
        createHeaderRow(['BUILDING SPECIFICATIONS', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.numberOfBuildings, proForma.buildings, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.averageStories, proForma.averageStories, true, EXCEL_NUM_FORMATS.NUMBER),
    ];

    if (isApartmentWithRetail && buildingInfoTab.buildingSpecs.apartmentGrossSF) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.apartmentGrossSF, baseGrossSF, true, EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    if (isApartmentWithRetail && buildingInfoTab.buildingSpecs.totalResidentialUnits) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.totalResidentialUnits,
            proForma.estimatedUnits,
            true,
            EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    if (isApartmentWithRetail && buildingInfoTab.buildingSpecs.retailGrossSF) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.retailGrossSF, retailGrossSF, true, EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    if (isApartmentWithRetail && buildingInfoTab.buildingSpecs.retailRentableSF) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.retailRentableSF, retailRentableSf, true, EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    if (isApartmentWithRetail && buildingInfoTab.buildingSpecs.totalRetailUnits) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.totalRetailUnits,
            retailUnitCount,
            true,
            EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    buildingSpecRows.push(
        createLabelValueRow(buildingInfoTab.buildingSpecs.grossSF, grossSF, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.rentableSF, combinedRentableSF, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.efficiencyRatio, efficiencyRatio, true, EXCEL_NUM_FORMATS.PERCENT),
    );

    if (!isApartmentWithRetail && buildingInfoTab.buildingSpecs.totalResidentialUnits) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.totalResidentialUnits,
            proForma.estimatedUnits,
            true,
            EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    if (buildingInfoTab.buildingSpecs.totalBeds) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.totalBeds,
            totalBeds,
            true,
            EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    buildingSpecRows.push(createBlankRow(2));

    // Parking Section
    const surfaceSpaces = proForma.surfaceParkingSpaces || 0;
    const structuredSpaces = proForma.structuredParkingSpaces || 0;
    const undergroundSpaces = proForma.undergroundParkingSpaces || 0;
    const trailerSpaces = proForma.trailerParkingSpaces || 0;
    const totalParkingSpaces = surfaceSpaces + structuredSpaces + undergroundSpaces + trailerSpaces;

    const parkingRows: ITableData[] = [
        createHeaderRow(['PARKING', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.parking.surfaceSpaces, surfaceSpaces, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.parking.structuredSpaces, structuredSpaces, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.parking.undergroundSpaces, undergroundSpaces, true, EXCEL_NUM_FORMATS.NUMBER),
    ];

    if (buildingInfoTab.parking.trailerSpaces) {
        parkingRows.push(createLabelValueRow(buildingInfoTab.parking.trailerSpaces, trailerSpaces, true, EXCEL_NUM_FORMATS.NUMBER));
    }

    if (buildingInfoTab.parking.parkingRatio) {
        const ratioDenominator = getParkingRatioDenominator(
            proForma.proFormaModelId, grossSF, totalRentableSf, proForma.estimatedUnits);
        if (ratioDenominator) {
            const parkingRatio = `${formatNumber(totalParkingSpaces / ratioDenominator, 1)} ${buildingInfoTab.parking.ratioLabel}`;
            parkingRows.push(createLabelValueRow(buildingInfoTab.parking.parkingRatio, parkingRatio, true));
        }
    }

    parkingRows.push(createBlankRow(2));

    // Key Dates
    const keyDatesRows: ITableData[] = [
        createHeaderRow(['KEY DATES', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.keyDates.analysisStart, formatDate(proForma.startDate), true),
        createLabelValueRow(buildingInfoTab.keyDates.constructionStart, formatDate(proForma.constructionStart), true),
        createLabelValueRow(buildingInfoTab.keyDates.constructionDuration, formatMonths(proForma.constructionDuration), true),
        createLabelValueRow(buildingInfoTab.keyDates.operationsStart, formatDate(operatingInfo?.operationStartDate), true),
        createLabelValueRow(buildingInfoTab.keyDates.holdPeriod, formatMonths(proForma.holdPeriod || 0), true),
        createLabelValueRow(buildingInfoTab.keyDates.projectedSaleDate, formatDate(proForma.saleDate), true),
        createBlankRow(2),
    ];

    const operationStart = operatingInfo?.operationStartDate
        ? new Date(operatingInfo.operationStartDate)
        : null;
    const operationStartYear = operationStart ? operationStart.getUTCFullYear() : null;
    const academicYearStartMonthLabel = operationStart
        ? operationStart.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long' })
        : '';

    // Operating Assumptions
    const operatingAssumptionsRows: ITableData[] = [
        createHeaderRow(['OPERATING ASSUMPTIONS', ''], EXCEL_COLORS.BLUE_HEADER),
    ];

    if (buildingInfoTab.operatingAssumptions.operationStartDate) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.operationStartDate,
            formatDate(operatingInfo?.operationStartDate),
            true,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.operationsStartYear) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.operationsStartYear,
            operationStartYear,
            true,
            EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.academicYearStartMonth) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.academicYearStartMonth,
            academicYearStartMonthLabel,
            true,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.occupancyUponStabilization) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.occupancyUponStabilization,
            parseNumber(operatingInfo?.occupancyUponStabilizationPercentage),
            true,
            EXCEL_NUM_FORMATS.PERCENT,
        ));
    }

    operatingAssumptionsRows.push(createLabelValueRow(
        buildingInfoTab.operatingAssumptions.rentGrowthAnnual,
        parseNumber(operatingInfo?.rentalGrowthPercentage),
        true,
        EXCEL_NUM_FORMATS.PERCENT,
    ));

    if (buildingInfoTab.operatingAssumptions.percentPreLeased) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.percentPreLeased,
            parseNumber(operatingInfo?.percentPreLeased),
            true,
            EXCEL_NUM_FORMATS.PERCENT,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.leaseUpPaceUnitsPerMonth) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.leaseUpPaceUnitsPerMonth,
            operatingInfo?.effectiveLeaseUpPace,
            true,
        ));
    }

    operatingAssumptionsRows.push(createLabelValueRow(
        buildingInfoTab.operatingAssumptions.lengthOfLease,
        formatMonths(operatingInfo?.lengthOfLease || 12),
        true,
    ));

    if (buildingInfoTab.operatingAssumptions.initialFreeRent) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.initialFreeRent,
            formatMonths(operatingInfo?.initialFreeRent || 0),
            true,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.stabilizedFreeRent) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.stabilizedFreeRent,
            formatMonths(operatingInfo?.stabilizedFreeRent || 0),
            true,
        ));
    }

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [],
                tableData: [
                    ...propertyInfoRows,
                    ...buildingSpecRows,
                    ...parkingRows,
                    ...keyDatesRows,
                    ...operatingAssumptionsRows,
                ],
            },
        ],
    };
};
